'use client';

import { useTranslations } from 'next-intl';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { HelpCircleIcon } from '@terra/ui-icons';
import { Button } from '../../components/button';
import { Checkbox } from '../../components/checkbox';
import { Field } from '../../components/field';
import { Input } from '../../components/input';
import { BodyDefault, MetaDefault } from '../../components/typography';
import { Tooltip } from '../../components/tooltip';

type McpOAuthAuthorizeAction = {
  onClick: () => void;
  disabled?: boolean;
};
type McpOAuthCredentialSectionProps = {
  credentialType: 'OAUTH' | 'OAUTH_DCR';
  isEditing: boolean;
  disabled?: boolean;
  oauthAuthorizeUrl: string;
  oauthTokenUrl: string;
  oauthClientId: string;
  oauthClientSecret: string;
  oauthScopes: string;
  availableOAuthScopes?: string[];
  isDiscoveringOAuthScopes?: boolean;
  authorizeAction?: McpOAuthAuthorizeAction;
  onOauthAuthorizeUrlChange: (value: string) => void;
  onOauthTokenUrlChange: (value: string) => void;
  onOauthClientIdChange: (value: string) => void;
  onOauthClientSecretChange: (value: string) => void;
  onOauthScopesChange: (value: string) => void;
  onOAuthScopeSelectionChange?: (scopes: string[]) => void;
};

function OAuthScopesFieldLabel({
  label,
  tooltip,
}: {
  label: string;
  tooltip: string;
}) {
  return (
    <HStack gap={1} alignItems="center">
      <Field.Label>{label}</Field.Label>
      <Tooltip title={tooltip} positioning={{ strategy: 'fixed' }}>
        <Box display="flex" color="fg.neutral.muted" cursor="help">
          <HelpCircleIcon size={16} />
        </Box>
      </Tooltip>
    </HStack>
  );
}

export function McpOAuthCredentialSection({
  credentialType,
  isEditing,
  disabled,
  oauthAuthorizeUrl,
  oauthTokenUrl,
  oauthClientId,
  oauthClientSecret,
  oauthScopes,
  availableOAuthScopes = [],
  isDiscoveringOAuthScopes = false,
  authorizeAction,
  onOauthAuthorizeUrlChange,
  onOauthTokenUrlChange,
  onOauthClientIdChange,
  onOauthClientSecretChange,
  onOauthScopesChange,
  onOAuthScopeSelectionChange,
}: McpOAuthCredentialSectionProps) {
  const t = useTranslations('designSystem.settings.mcpCredentialFields');
  const selectedScopes = oauthScopes
    .split(/[\s,]+/)
    .map((scope) => scope.trim())
    .filter(Boolean);
  const hasDiscoveredScopes = availableOAuthScopes.length > 0;
  const handleScopeToggle = ({
    scope,
    checked,
  }: {
    scope: string;
    checked: boolean;
  }) => {
    const nextScopes = new Set(selectedScopes);

    if (checked) {
      nextScopes.add(scope);
    } else {
      nextScopes.delete(scope);
    }

    const scopes = [...nextScopes];

    onOAuthScopeSelectionChange?.(scopes);
    onOauthScopesChange(scopes.join(' '));
  };

  return (
    <VStack gap={4} w="full">
      {credentialType === 'OAUTH' ? (
        <>
          <Field.Root w="full">
            <Field.Label>{t('fieldAuthUrl')}</Field.Label>
            <Input
              value={oauthAuthorizeUrl}
              onChange={(event) =>
                onOauthAuthorizeUrlChange(event.target.value)
              }
              disabled={disabled}
            />
          </Field.Root>
          <Field.Root w="full">
            <Field.Label>{t('fieldTokenUrl')}</Field.Label>
            <Input
              value={oauthTokenUrl}
              onChange={(event) => onOauthTokenUrlChange(event.target.value)}
              disabled={disabled}
            />
          </Field.Root>
          <Field.Root w="full">
            <Field.Label>{t('fieldClientId')}</Field.Label>
            <Input
              value={oauthClientId}
              onChange={(event) => onOauthClientIdChange(event.target.value)}
              disabled={disabled}
            />
          </Field.Root>
          <Field.Root w="full">
            <Field.Label>{t('fieldClientSecret')}</Field.Label>
            <Input
              type="password"
              value={oauthClientSecret}
              placeholder={isEditing ? t('placeholderKeepExisting') : undefined}
              onChange={(event) =>
                onOauthClientSecretChange(event.target.value)
              }
              disabled={disabled}
            />
          </Field.Root>
        </>
      ) : null}

      {hasDiscoveredScopes ? (
        <VStack alignItems="stretch" gap={2} w="full">
          <OAuthScopesFieldLabel
            label={t('fieldOAuthScopes')}
            tooltip={t('fieldOAuthScopesTooltip')}
          />
          {isDiscoveringOAuthScopes ? (
            <BodyDefault color="fg.muted">
              {t('oauthScopesDiscovering')}
            </BodyDefault>
          ) : null}
          {availableOAuthScopes.map((scope) => (
            <Checkbox
              key={scope}
              checked={selectedScopes.includes(scope)}
              disabled={disabled}
              onCheckedChange={(details) =>
                handleScopeToggle({
                  scope,
                  checked: details.checked === true,
                })
              }
            >
              {scope}
            </Checkbox>
          ))}
          <MetaDefault color="fg.muted">
            {t('oauthScopesDiscoveredHint')}
          </MetaDefault>
        </VStack>
      ) : (
        <Field.Root w="full">
          <OAuthScopesFieldLabel
            label={t('fieldOAuthScopes')}
            tooltip={t('fieldOAuthScopesTooltip')}
          />
          {isDiscoveringOAuthScopes ? (
            <BodyDefault color="fg.muted">
              {t('oauthScopesDiscovering')}
            </BodyDefault>
          ) : null}
          <Input
            value={oauthScopes}
            onChange={(event) => onOauthScopesChange(event.target.value)}
            disabled={disabled}
            placeholder={t('oauthScopesManualPlaceholder')}
          />
        </Field.Root>
      )}

      {credentialType === 'OAUTH_DCR' ? (
        <MetaDefault color="fg.muted">{t('oauthDcrNote')}</MetaDefault>
      ) : null}

      {authorizeAction ? (
        <Button
          variant="outline"
          size="sm"
          alignSelf="flex-start"
          disabled={authorizeAction.disabled}
          onClick={authorizeAction.onClick}
        >
          {authorizeAction.disabled
            ? t('oauthAuthorizeLoading')
            : t('oauthAuthorize')}
        </Button>
      ) : null}
    </VStack>
  );
}
